import styles from "./styles.css";
import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { NUMBER } from "../../../constants/app-constants";
import MakeModalSearchList from "../make-modal-search-list";

const Dropdown = ({
    options,
    value,
    onChange,
    placeholder = "Search ...",
    disabled = false,
    isRevamped = false
}) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const inputRef = useRef(null);

    useEffect(() => {
        if (value && value.label) {
            setSearchInput(value.label);
        } else {
            setSearchInput("");
        }
    }, [value]);

    const sortedOptions = useMemo(() => {
        if (!isRevamped) return options;
        const copy = Array.isArray(options) ? options.slice() : [];
        return copy.sort((a, b) => (a?.label || "").localeCompare(b?.label || "", undefined, { sensitivity: "base" }));
    }, [options, isRevamped]);

    const filteredOptions = useMemo(() => {
        if (!isRevamped) return sortedOptions;
        const q = (searchInput || "").trim().toLowerCase();
        if (!q) return sortedOptions;
        return sortedOptions.filter(opt => (opt?.label || "").toLowerCase().includes(q));
    }, [sortedOptions, searchInput, isRevamped]);

    const handleFilterSelect = async (option, index, event) => {
        event.stopPropagation();
        if (disabled) return;
        const payload = (isRevamped ? sortedOptions : options).find((item) => item.label === option.displayTitle);
        onChange(payload);
        setIsDropdownOpen(false);
        if (inputRef.current) {
            inputRef.current.blur();
        }
    };

    const dropdownOptions = filteredOptions.map((option) => ({
        displayTitle: option.label
    }));

    const onWrapperClick = () => {
        if (disabled || !isRevamped) return;
        setIsDropdownOpen(true);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const onInputClick = () => {
        if (disabled) return;
        if (!isRevamped) {
            setIsDropdownOpen(!isDropdownOpen);
        }
    };

    const onInputChange = (e) => {
        if (disabled || !isRevamped) return;
        const val = e.target.value;
        setSearchInput(val);
        if (!isDropdownOpen) setIsDropdownOpen(true);
    };

    const onBlurCallback = async () => {
        window.setTimeout(() => {
            setIsDropdownOpen(false);
        }, NUMBER.HUNDRED);
    };

    return (
        <div styleName={`styles.wrapper ${isRevamped ? "styles.revamped" : ""}`} onClick={onWrapperClick}>
            <input
                ref={inputRef}
                placeholder={placeholder}
                value={searchInput}
                onChange={onInputChange}
                onClick={onInputClick}
                onBlur={onBlurCallback}
                disabled={disabled}
                autoComplete="off"
                inputMode={isRevamped ? "text" : "none"}
                readOnly={!isRevamped}
            />
            {
                isDropdownOpen && !disabled && (
                    <div
                        styleName="styles.dropdown"
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        <MakeModalSearchList
                            searchSuggestions={dropdownOptions}
                            handleSearchSuggestionSelect={handleFilterSelect}
                            searchInput={isRevamped ? searchInput : ""}
                            shouldShowSearchIcon={false}
                            shouldShowEmptyState={true}
                            emptyStateText={`No results found for ${searchInput}`}
                        />
                    </div>
                )}
        </div>
    );
};

Dropdown.propTypes = {
    options: PropTypes.array,
    value: PropTypes.object,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    dropdownClassName: PropTypes.string,
    disabled: PropTypes.bool,
    isRevamped: PropTypes.bool
};

export default Dropdown;
